<template>
	<div class="tabs mb--2" @click.stop.prevent="handleClick">
		<nav class="tabs__nav">
			<button role="button" v-for="(tab) in tabs" v-bind:key="tab.title" class="tabs__nav-item txt--strong" v-bind:class="{'tabs__nav-item--active bg--primary': tab.display}" v-on:click="selectTab(tab)">
				<span v-html="tab.title">Link</span>					
				<i v-if="!tab.display" aria-hidden="true" class="fa fa-chevron-down ml-1 ml--1 show--md"></i>
				<i v-if="tab.display" aria-hidden="true" class="fa fa-chevron-up ml-1 ml--1 show--md"></i>	
			</button>
		</nav>
		<div class="tabs__content" id="tabcontent">
			<div v-for="(tab) in tabs" v-bind:key="tab.content">
				<div v-if="tab.display" class="bdr--light-gray p--1 wysiwyg" role="tabpanel" :aria-labelledby="tab.title" v-html="tab.content"></div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: "Info",
	methods: {
		selectTab(selectedTab) {
			this.tabs.forEach(tab => {
				if(tab.display) {
					tab.display = false;
				} else {
					tab.display = (tab.title == selectedTab.title);
				}
			});
		},		
				
		handleClick(e) {
			if (e.target.matches("a")) {
				let url = e.target.href;
				let target = e.target.target == "_blank" ? "_blank" : "_self";
				let dataId = e.target.dataset.id == null ? -1 : e.target.dataset.id;
				var isnativeapp = document.getElementById("isnativeapp");
				if (isnativeapp === undefined || isnativeapp === null) 
				{
					window.open(url,target);  
				}
				else{
					if(target == "_blank") {
						let data = {title:'',nodeid: dataId, href:url, target: target};
						window.Native('dataCallback', data);
					} 
				}	
			}
		},
	},
	data() {
		return {
			tabs: [
				{
					title: "When to use",
					display: false,
					content: `
						<p>The 4AT is designed to be used by any health professional at first contact with the patient, and at other times when delirium is suspected.</p>
						<p>The 4AT is not designed for repeated daily or more monitoring for new onset delirium for periods of more than a small number of days (because of practice effects with the Months Backwards task). It may be useful to repeat the 4AT in active delirium to monitor for recovery.</p>
						<p>Altered level of arousal (Item 1), and change or fluctuation (Item 4) can be used for daily or even more frequent monitoring, along with general clinical judgement and/or use of tools specifically designed for ongoing monitoring of delirium. <a href="https://www.the4at.com/references" target="_blank">See references</a></p>
						<p>Note that some patients may be too unwell/drowsy/distressed/hyperactive to engage meaningfully with cognitive testing or interview; note that the 4AT items AMT4 and Attention (Months Backwards) should still be scored in this situation, using the ‘untestable’ category.</p>
					`
				},
				{
					title: "Evidence",
					display: false,
					content: `
						<p>The 4AT is among the best-validated delirium tools in the literature, with multiple  validation studies involving >4000 patients. <a href="https://www.the4at.com/references" target="_blank">See references</a></p>
						<p><strong>Key features of the 4AT: </strong></p>
						<ul>
							<li>takes <2 minutes </li>
							<li>suitable for use in normal clinical practice </li>
							<li>practical and simple</li>
							<li>no special training is required</li>
							<li>all patients can be assessed, including those unable to communicate (eg. patients with severe drowsiness or agitation)</li>
							<li>includes brief cognitive tests</li>
						</ul>
						<p>The 4AT was originally designed in 2011 by Prof Alasdair MacLullich (Edinburgh Delirium Research Group, University of Edinburgh, Scotland), and Dr Tracy Ryan and Dr Helen Cash (NHS Lothian, Scotland). Updates since then have been carried out by and are the responsibility of Alasdair MacLullich.</p>
					`
				},
				{
					title: "Calculation<span class='show--md'>  & Interpretation</span>",
					display: false,
					content: `						
						<p>Scores are assigned to responses to the 4AT criteria as indicated below.</p>
						<h4 class="txt--xl">1. Alertness</h4>
						<p>This includes patients who may be markedly drowsy (eg. difficult to rouse and/or obviously sleepy during assessment) or agitated/hyperactive. Observe the patient. If asleep, attempt to wake with speech or gentle touch on shoulder. Ask the patient to state their name and address to assist rating.</p>
						<table class="tbl table" role="presentation">
							<tr>
								<th>Input value</th>
								<th width="150">Score</th>
							</tr>
							<tr>
								<td valign="top">Normal (fully alert, but not agitated, throughout assessment)</td>
								<td valign="top">
									0
								</td>
							</tr>
							<tr>
								<td valign="top">Mild sleepiness for <10 seconds after waking, then normal</td>
								<td valign="top">
									0
								</td>
							</tr>
							<tr>
								<td valign="top">Clearly abnormal</td>
								<td valign="top">
									+4
								</td>
							</tr>	
						</table>
						<h4 class="txt--xl">2. AMT4</h4>
						<p>Age, date of birth, place (name of the hospital or building), current year.</p>
						<table class="tbl table" role="presentation">
							<tr>
								<th>Input value</th>
								<th width="150">Score</th>
							</tr>
							<tr>
								<td valign="top">No mistakes</td>
								<td valign="top">
									0
								</td>
							</tr>
							<tr>
								<td valign="top">1 mistake</td>
								<td valign="top">
									+1
								</td>
							</tr>
							<tr>
								<td valign="top">2 or more mistakes/untestable</td>
								<td valign="top">
									+2
								</td>
							</tr>	
						</table>
						<h4 class="txt--xl">3. Attention</h4>
						<p>Ask the patient: “Please tell me the months of the year in backwards order, starting at December.” To assist initial understanding one prompt of “what is the month before December?” is permitted. </p>
						<p>Months of the year backwards.</p>
						<table class="tbl table" role="presentation">
							<tr>
								<th>Input value</th>
								<th width="150">Score</th>
							</tr>
							<tr>
								<td valign="top">Achieves 7 months or more correctly</td>
								<td valign="top">
									0
								</td>
							</tr>
							<tr>
								<td valign="top">Starts but scores <7 months / refuses to start</td>
								<td valign="top">
									+1
								</td>
							</tr>
							<tr>
								<td valign="top">Untestable (cannot start because unwell, drowsy, inattentive)</td>
								<td valign="top">
									+2
								</td>
							</tr>	
						</table>
						<h4 class="txt--xl">4. Acute change or fluctuating course</h4>
						<p>Evidence of significant change or fluctuation in: alertness, cognition, other mental function (eg. paranoia, hallucinations) arising over the last 2 weeks and still evident in last 24hrs  </p>
						<table class="tbl table" role="presentation">
							<tr>
								<th>Input value</th>
								<th width="150">Score</th>
							</tr>
							<tr>
								<td valign="top">No</td>
								<td valign="top">
									0
								</td>
							</tr>
							<tr>
								<td valign="top">Yes</td>
								<td valign="top">
									+4
								</td>
							</tr>
						</table>
						<p>Scores are added and interpreted as follows: </p>
						<table class="tbl table">
							<tr>
								<th valign="bottom" width="110">Total Score</th>
								<th valign="bottom">Result</th>
							</tr>
							<tr>
								<td valign="top">
									4 or above 
								</td>
								<td valign="top">
									possible delirium +/- cognitive impairment 
								</td>
							</tr>
							<tr>
								<td valign="top">
									1-3
								</td>
								<td valign="top">
									possible cognitive impairment 
								</td>
							</tr>							
							<tr>
								<td valign="top">
									0
								</td>
								<td valign="top">
									delirium or severe cognitive Impairment unlikely (but delirium still possible if answer to question 4 (Acute change or fluctuating course) incomplete)
								</td>
							</tr>
						</table>
					`
				},
				{
					title: "Practice<span class='show--md'>  considerations</span>",
					display: false,
					content	: `
						<ul>
							<li>Screens with higher sensitivity in patients without dementia. </li>
							<li>No consensus exists for the assessment of inattention. </li>
							<li>Limited external validation in some small populations, including the acute stroke setting. </li>
							<li>Validated in non-English speaking patients with 91% sensitivity (<a href="https://onlinelibrary.wiley.com/doi/abs/10.1002/gps.4615" target="_blank">De 2016</a>). </li>
						</ul>
					`    
				}
			]
		};
	},
};
</script>

